/**
 * Analytics page taxonomy — single source of truth for page grouping.
 *
 * Verified against every page.tsx route under app. A CI check
 * (scripts/check-route-taxonomy.js, `npm run taxonomy:check`) scans the
 * route files and fails if any route maps to `other`, so this table cannot
 * drift silently. `route_group = other` in PostHog means "add a row here".
 *
 * Sections roll groups up for high-level reporting. Patterns use Next
 * bracket syntax; matching is trailing-slash tolerant and ignores query
 * strings (query-only changes are not pageviews).
 */

export type RouteTaxonomyEntry = {
  pattern: string;
  group: string;
  section: string;
};

export const ROUTE_TAXONOMY: RouteTaxonomyEntry[] = [
  // Home
  { pattern: '/', group: 'home', section: 'home' },
  // Discovery
  { pattern: '/properties', group: 'properties_list', section: 'discovery' },
  { pattern: '/properties/[...segments]', group: 'properties_seo_directory', section: 'discovery' },
  { pattern: '/request-property', group: 'request_property', section: 'discovery' },
  { pattern: '/price-index', group: 'price_index', section: 'discovery' },
  // Property
  { pattern: '/property/[slug_or_uuid]', group: 'property_detail', section: 'property' },
  // People
  { pattern: '/realtors', group: 'realtors_list', section: 'people' },
  { pattern: '/realtors/state/[state]', group: 'realtors_by_state', section: 'people' },
  { pattern: '/realtors/[realtorid]', group: 'realtor_detail', section: 'people' },
  { pattern: '/realtors/[realtorid]/[propertyid]', group: 'realtor_property_alias', section: 'people' },
  { pattern: '/developers', group: 'developers_list', section: 'people' },
  { pattern: '/developers/state/[state]', group: 'developers_by_state', section: 'people' },
  { pattern: '/developers/[developerid]', group: 'developer_detail', section: 'people' },
  // Deals & investments
  { pattern: '/weekly-deals', group: 'weekly_deals', section: 'deals' },
  { pattern: '/managed-properties', group: 'managed_properties', section: 'deals' },
  { pattern: '/managed-developments', group: 'managed_developments', section: 'deals' },
  { pattern: '/investment/[id]', group: 'investment_detail', section: 'deals' },
  // Subscription & conversion
  { pattern: '/realtor-upgrade', group: 'realtor_upgrade', section: 'subscription' },
  { pattern: '/realtor-upgrade-old', group: 'realtor_upgrade_legacy', section: 'subscription' },
  // Transactions
  { pattern: '/payment/callback/[reference]', group: 'payment_callback', section: 'transactions' },
  { pattern: '/payment/callback/service/[reference]', group: 'payment_service_callback', section: 'transactions' },
  // Dashboard
  { pattern: '/user/dashboard', group: 'dashboard_overview', section: 'dashboard' },
  { pattern: '/user/dashboard/investments', group: 'dashboard_investments', section: 'dashboard' },
  { pattern: '/user/dashboard/payments', group: 'dashboard_payments', section: 'dashboard' },
  { pattern: '/user/dashboard/profile', group: 'dashboard_profile', section: 'dashboard' },
  { pattern: '/user/dashboard/properties', group: 'dashboard_properties', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/ads-manager', group: 'dashboard_ads_manager', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/customers-request', group: 'dashboard_customers_request', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/my-developments', group: 'dashboard_my_developments', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/my-listings', group: 'dashboard_my_listings', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/my-projects', group: 'dashboard_my_projects', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/my-subscriptions', group: 'dashboard_my_subscriptions', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/property-inspection', group: 'dashboard_property_inspection', section: 'dashboard' },
  { pattern: '/user/dashboard/properties/saved-properties', group: 'dashboard_saved_properties', section: 'dashboard' },
  // Auth
  { pattern: '/auth/sign-in', group: 'auth_signin', section: 'auth' },
  { pattern: '/auth/sign-up', group: 'auth_signup', section: 'auth' },
  { pattern: '/auth/onboarding', group: 'auth_onboarding', section: 'auth' },
  { pattern: '/auth/verify', group: 'auth_verify', section: 'auth' },
  { pattern: '/auth/forgot-password', group: 'auth_forgot_password', section: 'auth' },
  { pattern: '/auth/reset-password', group: 'auth_reset_password', section: 'auth' },
  // Content
  { pattern: '/blog', group: 'blog_list', section: 'content' },
  { pattern: '/blog/[slug]', group: 'blog_post', section: 'content' },
  { pattern: '/contact', group: 'contact', section: 'content' },
  // Legal & lifecycle
  { pattern: '/privacy-policy', group: 'legal_privacy', section: 'legal' },
  { pattern: '/terms-and-conditions', group: 'legal_terms', section: 'legal' },
  { pattern: '/cookie-policy', group: 'legal_cookies', section: 'legal' },
  { pattern: '/mou-agreement', group: 'legal_mou', section: 'legal' },
  { pattern: '/email/unsubscribe', group: 'email_unsubscribe', section: 'legal' },
  // Legacy in-app admin screens (the admin app lives separately).
  { pattern: '/admin/analytics', group: 'admin_analytics', section: 'admin' },
  { pattern: '/admin/auth/sign-in', group: 'admin_signin', section: 'admin' },
  { pattern: '/admin/blog/create', group: 'admin_blog_create', section: 'admin' },
  { pattern: '/admin/user/dashboard', group: 'admin_dashboard', section: 'admin' },
];

const patternToRegExp = (pattern: string): RegExp => {
  const escaped = pattern
    .split('/')
    .map((segment) => {
      if (segment.startsWith('[...') && segment.endsWith(']')) return '.*';
      if (segment.startsWith('[') && segment.endsWith(']')) return '[^/]+';
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return new RegExp(`^${escaped}/?$`);
};

const matchers = ROUTE_TAXONOMY.map((entry) => ({
  entry,
  regex: patternToRegExp(entry.pattern),
}));

export const normalizePathname = (pathname: string): string => {
  const path = pathname.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
};

const matchEntry = (pathname: string): RouteTaxonomyEntry | null => {
  const path = normalizePathname(pathname);
  for (const { entry, regex } of matchers) {
    if (regex.test(path)) return entry;
  }
  return null;
};

/** Curated friendly family name, or 'other' (data-quality signal). */
export const routeGroupForPathname = (pathname: string): string =>
  matchEntry(pathname)?.group ?? 'other';

/** High-level roll-up for executive reporting. */
export const routeSectionForPathname = (pathname: string): string =>
  matchEntry(pathname)?.section ?? 'other';

/** Next-style pattern (/properties/[id]) or null when unmapped. */
export const routePatternForPathname = (pathname: string): string | null =>
  matchEntry(pathname)?.pattern ?? null;

/**
 * Query params that must never reach PostHog (tokens, identifiers, payment
 * references). Blocklist lives here so capture sites and before_send share it.
 */
export const SENSITIVE_QUERY_PARAMS = [
  'token',
  'code',
  'otp',
  'email',
  'reference',
  'payment_reference',
  'transaction_id',
  'idempotency_key',
  'guest_id',
] as const;

export const scrubUrl = (rawUrl: string): string => {
  try {
    const url = new URL(rawUrl);
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (url.searchParams.has(param)) url.searchParams.set(param, '[redacted]');
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
};
